import React from 'react';

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  webpSrc?: string; // Optional: explicitly provide WebP source (single resolution)
  srcSet?: string; // Optional: responsive srcset string (e.g. via vite-imagetools `?as=srcset`)
  sizes?: string; // Optional: sizes attribute paired with srcSet
  alt: string;
  width?: number;
  height?: number;
  priority?: boolean;
  className?: string;
}

/**
 * OptimizedImage component
 *
 * Renders a <picture> with optional WebP <source>(s) and a fallback <img>.
 * Supports both single-resolution WebP (legacy) and responsive srcset
 * (via vite-imagetools query params).
 *
 * Performance:
 *   - lazy by default; pass `priority` for above-the-fold (LCP) images
 *   - `decoding="async"` for non-blocking image decoding
 *   - `fetchPriority="high"` is set automatically when `priority` is true
 *
 * Responsive usage (recommended for content images):
 *   import imgSrcset from '@/assets/foo.webp?w=480;800;1200&format=webp&as=srcset';
 *   import imgSrc from '@/assets/foo.webp?w=1200&format=webp';
 *   <OptimizedImage
 *     src={imgSrc}
 *     srcSet={imgSrcset}
 *     sizes="(max-width: 768px) 100vw, 800px"
 *     alt="..."
 *     width={1200}
 *     height={900}
 *   />
 *
 * Legacy usage (single resolution WebP):
 *   <OptimizedImage src={imgPng} webpSrc={imgWebp} alt="..." width={...} height={...} />
 */
const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  webpSrc,
  srcSet,
  sizes,
  alt,
  width,
  height,
  priority = false,
  className = '',
  ...props
}) => {
  const getWebPSrc = (): string | undefined => {
    if (webpSrc) {
      return webpSrc;
    }
    if (typeof src === 'string' && !src.includes('?') && !src.match(/\/assets\/.*-[a-f0-9]+\./)) {
      return src.replace(/\.(png|jpg|jpeg)$/i, '.webp');
    }
    return undefined;
  };

  const webpSource = getWebPSrc();

  return (
    <picture>
      {srcSet ? (
        <source srcSet={srcSet} sizes={sizes} type="image/webp" />
      ) : (
        webpSource && <source srcSet={webpSource} type="image/webp" />
      )}
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        // @ts-expect-error fetchpriority is a valid HTML attr; React types lag behind
        fetchpriority={priority ? 'high' : undefined}
        className={className}
        {...props}
      />
    </picture>
  );
};

export default OptimizedImage;

